"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { useAccount } from "#/store/wallet/account/account";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { EIP712Domain } from "#/utils/signature";

import { cancelOrders } from "../kyan.service";
import {
  type CancelOrdersRequest,
  type CancelOrdersResponse,
  type WithoutSignature,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

const CancelOrdersType: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "maker", type: "address" },
  { name: "orderIds", type: "string[]" },
];

const signCancelOrder = async (
  cancelRequest: Omit<CancelOrdersRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<CancelOrdersRequest> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...cancelRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { CancelOrdersType },
      primaryType: "CancelOrdersType",
      message: {
        deadline: signature_deadline,
        maker: cancelRequest.maker,
        orderIds: cancelRequest.order_ids,
      },
    }),
  };
};

export const useCancelOrders = (
  options?: UseMutationOptions<
    CancelOrdersResponse,
    Error,
    Pick<CancelOrdersRequest, "order_ids">
  >,
) => {
  const { account } = useAccount();
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async ({ order_ids }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const data: WithoutSignature<CancelOrdersRequest> = {
        maker: account!,
        order_ids,
      };

      if (shouldUseSessionHash) {
        return defaultApiResponseHandler({
          serviceName: "Cancel Orders",
          payload: { data, includeSessionHash: true },
          service: cancelOrders,
        });
      }

      const signedCancelOrder = await signCancelOrder(
        data,
        walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
      );

      return defaultApiResponseHandler({
        serviceName: "Cancel Orders",
        payload: { data: signedCancelOrder },
        service: cancelOrders,
      });
    },
  });
};
